import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { AppState } from 'react-native';
import * as Network from 'expo-network';
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { deferSupabaseAuthCallbackWork } from './deferSupabaseAuthCallbackWork';

export type SubscriptionStatus =
    | 'connecting'
    | 'connected'
    | 'error'
    | 'reconnecting'
    | 'disconnected';

export interface SubscriptionError {
    message: string;
    timestamp: Date;
}

export interface SubscriptionConfig {
    channelName: string;
    subscriptions: Array<{
        event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
        schema: string;
        table: string;
        filter?: string;
        callback: (payload: any) => void;
    }>;
}

type SubscriptionContextType = {
    subscriptionStatus: SubscriptionStatus;
    subscriptionError: SubscriptionError | null;
    retrySubscription: () => Promise<void>;
    setupSubscription: (config: SubscriptionConfig) => Promise<void>;
    teardownSubscription: () => Promise<void>;
};

/**
 * Factory function to create a subscription provider for any Supabase client
 * @param supabaseClient - The Supabase client instance (remote or local)
 * @param contextName - Name for debugging/logging purposes
 * @returns Provider component and useSubscription hook
 */
export function createSubscriptionProvider(
    supabaseClient: SupabaseClient<any>,
    contextName: string = 'Subscription'
) {
    const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

    function SubscriptionProvider({ children }: { children: ReactNode }) {
        const subscriptionRef = useRef<RealtimeChannel | null>(null);
        const configRef = useRef<SubscriptionConfig | null>(null);
        const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('disconnected');
        const [subscriptionError, setSubscriptionError] = useState<SubscriptionError | null>(null);
        const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

        const teardownSubscription = useCallback(async () => {
            if (subscriptionRef.current) {
                console.log(`🧹 [${contextName}] Tearing down subscription.`);
                await supabaseClient.removeChannel(subscriptionRef.current);
                subscriptionRef.current = null;
            }
            
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            
            if (connectionCheckIntervalRef.current) {
                clearInterval(connectionCheckIntervalRef.current);
                connectionCheckIntervalRef.current = null;
            }
            
            setSubscriptionStatus('disconnected');
            setSubscriptionError(null);
        }, []);

        const setupSubscription = useCallback(async (config: SubscriptionConfig) => {
            // Store config for recovery
            configRef.current = config;

            // First tear down any existing subscription
            await teardownSubscription();

            console.log(`🔄 [${contextName}] Setting up new subscription:`, config.channelName);
            setSubscriptionStatus('connecting');
            setSubscriptionError(null);

            try {
                const channel = supabaseClient.channel(config.channelName);

                // Add all the subscriptions
                config.subscriptions.forEach(sub => {
                    channel.on('postgres_changes' as any, {
                        event: sub.event,
                        schema: sub.schema,
                        table: sub.table,
                        ...(sub.filter && { filter: sub.filter })
                    }, sub.callback);
                });

                // Subscribe with status handling
                channel.subscribe((status, err) => {
                    const logPrefix = `[${contextName}]`;
                    console.log(`${logPrefix} Subscription status:`, status);

                    switch (status) {
                        case 'SUBSCRIBED':
                            console.log(`${logPrefix} ✅ Successfully subscribed!`);
                            setSubscriptionStatus('connected');
                            setSubscriptionError(null);
                            break;
                        case 'CHANNEL_ERROR':
                            console.error(`${logPrefix} 💥 Channel error.`, err);
                            setSubscriptionStatus('error');
                            setSubscriptionError({
                                message: err?.message || 'Channel error',
                                timestamp: new Date()
                            });
                            // Schedule reconnection attempt
                            scheduleReconnection();
                            break;
                        case 'TIMED_OUT':
                            console.warn(`${logPrefix} ⏱️ Subscription timed out`);
                            setSubscriptionStatus('error');
                            setSubscriptionError({
                                // i18n-ignore: technical-token -- diagnostic state is logged, never rendered.
                                message: 'Subscription timed out',
                                timestamp: new Date()
                            });
                            // Schedule reconnection attempt
                            scheduleReconnection();
                            break;
                        case 'CLOSED':
                            console.log(`${logPrefix} 🚪 Channel closed`);
                            setSubscriptionStatus('disconnected');
                            break;
                    }
                });

                subscriptionRef.current = channel;
            } catch (error) {
                console.error(`[${contextName}] Failed to setup subscription:`, error);
                setSubscriptionStatus('error');
                setSubscriptionError({
                    message: error instanceof Error ? error.message : 'Failed to setup subscription',
                    timestamp: new Date()
                });
            }
        }, [teardownSubscription]);

        const scheduleReconnection = useCallback(() => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            
            reconnectTimeoutRef.current = setTimeout(() => {
                console.log(`🔄 [${contextName}] Attempting automatic reconnection...`);
                retrySubscription();
            }, 5000); // Retry after 5 seconds
        }, []);

        const retrySubscription = useCallback(async () => {
            console.log(`🔄 [${contextName}] Manual retry requested`);
            
            if (!configRef.current) {
                console.log(`🔄 [${contextName}] No subscription config available for retry`);
                setSubscriptionError({
                    // i18n-ignore: technical-token -- diagnostic state is logged, never rendered.
                    message: 'No subscription configuration',
                    timestamp: new Date()
                });
                return;
            }

            setSubscriptionStatus('reconnecting');
            await setupSubscription(configRef.current);
        }, [setupSubscription]);

        // Periodic connection state monitoring
        useEffect(() => {
            if (!configRef.current) return;

            const checkConnection = () => {
                const realtimeClient = (supabaseClient as any).realtime;
                if (!realtimeClient) return;

                const isConnected = realtimeClient.isConnected?.() ?? false;
                const connectionState = realtimeClient.connectionState?.() ?? 'unknown';

                // Detect disconnect
                if (!isConnected && subscriptionStatus === 'connected') {
                    console.log(`🔴 [${contextName} Connection Monitor] Connection lost!`);
                    setSubscriptionStatus('error');
                    setSubscriptionError({
                        // i18n-ignore: technical-token -- diagnostic state is logged, never rendered.
                        message: 'Connection lost',
                        timestamp: new Date()
                    });
                    scheduleReconnection();
                }
                // Detect reconnect
                else if (isConnected && (subscriptionStatus === 'error' || subscriptionStatus === 'disconnected')) {
                    console.log(`🟢 [${contextName} Connection Monitor] Connection restored!`);
                    setSubscriptionStatus('reconnecting');
                    if (configRef.current) {
                        setupSubscription(configRef.current);
                    }
                }
            };

            // Check every 3 seconds
            connectionCheckIntervalRef.current = setInterval(checkConnection, 3000);

            // Check on app state change (foreground/background)
            const handleAppStateChange = (nextAppState: string) => {
                if (nextAppState === 'active') {
                    console.log(`📱 [${contextName} AppState] App became active, checking connection...`);
                    checkConnection();
                }
            };

            const appStateSubscription = AppState.addEventListener('change', handleAppStateChange);

            return () => {
                if (connectionCheckIntervalRef.current) {
                    clearInterval(connectionCheckIntervalRef.current);
                }
                appStateSubscription.remove();
            };
        }, [subscriptionStatus, setupSubscription, scheduleReconnection]);

        // Network connectivity monitoring
        useEffect(() => {
            const handleNetworkChange = (state: Network.NetworkState) => {
                console.log(`🔌 [${contextName} Network] State changed:`, state);

                if (!state.isConnected && subscriptionStatus === 'connected') {
                    console.log(`🔌 [${contextName} Network] Connection lost`);
                    setSubscriptionStatus('error');
                    setSubscriptionError({
                        // i18n-ignore: technical-token -- diagnostic state is logged, never rendered.
                        message: 'Network offline',
                        timestamp: new Date()
                    });
                } else if (state.isConnected && subscriptionStatus === 'error') {
                    console.log(`🔌 [${contextName} Network] Connection restored`);
                    // Connection monitoring will handle reconnection
                }
            };

            const subscription = Network.addNetworkStateListener(handleNetworkChange);

            return () => {
                subscription.remove();
            };
        }, [subscriptionStatus]);

        // Listen to auth state changes
        useEffect(() => {
            console.log(`🔐 [${contextName}] Setting up auth state listener`);

            let mounted = true;

            const { data: { subscription: authSubscription } } = supabaseClient.auth.onAuthStateChange(
                (event, session) => {
                    if (!mounted) return;

                    console.log(`🔐 [${contextName}] Auth state changed:`, event, 'User ID:', session?.user?.id);

                    switch (event) {
                        case 'TOKEN_REFRESHED':
                            if (session?.user?.id && subscriptionRef.current === null && configRef.current) {
                                console.log(`🔐 [${contextName}] Token refreshed and no active subscription, setting up...`);
                                const config = configRef.current;
                                deferSupabaseAuthCallbackWork(`${contextName} subscription setup after token refresh`, async () => {
                                    if (!mounted || subscriptionRef.current !== null) {
                                        return;
                                    }

                                    await setupSubscription(config);
                                });
                            }
                            break;

                        case 'SIGNED_OUT':
                            console.log(`🔐 [${contextName}] User signed out, tearing down subscription`);
                            configRef.current = null;
                            deferSupabaseAuthCallbackWork(`${contextName} subscription teardown after sign out`, async () => {
                                if (!mounted) {
                                    return;
                                }

                                await teardownSubscription();
                            });
                            break;

                        case 'USER_UPDATED':
                            console.log(`🔐 [${contextName}] User updated`);
                            // Check if we need to set up subscription
                            if (session?.user?.id && subscriptionRef.current === null && configRef.current) {
                                console.log(`🔐 [${contextName}] No active subscription, setting up...`);
                                const config = configRef.current;
                                deferSupabaseAuthCallbackWork(`${contextName} subscription setup after user update`, async () => {
                                    if (!mounted || subscriptionRef.current !== null) {
                                        return;
                                    }

                                    await setupSubscription(config);
                                });
                            }
                            break;
                    }
                }
            );

            return () => {
                mounted = false;
                console.log(`🔐 [${contextName}] Cleaning up`);
                authSubscription.unsubscribe();
                teardownSubscription();
            };
        }, [setupSubscription, teardownSubscription]);

        // Log subscription status changes
        useEffect(() => {
            console.log(`📊 [${contextName}] Status changed:`, subscriptionStatus);
            if (subscriptionError) {
                console.log(`📊 [${contextName}] Error:`, subscriptionError);
            }
        }, [subscriptionStatus, subscriptionError]);

        const value = {
            subscriptionStatus,
            subscriptionError,
            retrySubscription,
            setupSubscription,
            teardownSubscription,
        };

        return (
            <SubscriptionContext.Provider value={value}>
                {children}
            </SubscriptionContext.Provider>
        );
    }

    function useSubscription() {
        const context = useContext(SubscriptionContext);
        if (context === undefined) {
            throw new Error(`useSubscription must be used within a ${contextName}Provider`);
        }
        return context;
    }

    return { SubscriptionProvider, useSubscription };
}
